// Phone-sized surface for company admins (see `features/mobile-admin.md`).
// Replaces the desktop tab layout with a compact shell: a slim AppBar (brand,
// company, language, escape hatch to the full site, logout) over one of three
// bottom-navigation tabs — Attendance (week calendar + marking), Athletes and
// Coaches (searchable rosters with registration). Reads the company's roster
// and training-class snapshot here so the tabs share one load; the server
// still enforces every permission on each request.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import GroupsIcon from '@mui/icons-material/Groups';
import SportsIcon from '@mui/icons-material/Sports';
import type {
  Athlete,
  Company,
  CompanySeatUsage,
  TrainingClass,
  TrainingClassEnrollment,
} from '../domain';
import type { TrainingClassesSnapshot } from '../data/apiClient';
import {
  fetchAthletes,
  fetchCompanySeatUsage,
  fetchTrainingClasses,
} from '../data/apiClient';
import { useAuth } from '../state/AuthContext';
import { useTranslation } from '../state/I18nContext';
import { BrandLogo } from './BrandLogo';
import { AccountMenu } from './AccountMenu';
import { LanguageSelector } from './LanguageSelector';
import { MobileAttendanceView } from './MobileAttendanceView';
import { MobileRosterView } from './MobileRosterView';
import { SeatUsageSummary } from './SeatUsageSummary';

type MobileTab = 'attendance' | 'athletes' | 'coaches';

interface MobileAdminViewProps {
  /** The admin's accessible companies (non-empty; the first is the default). */
  companies: Company[];
  /** Switch back to the full desktop layout for this session. */
  onFullSite: () => void;
}

export function MobileAdminView({ companies, onFullSite }: MobileAdminViewProps) {
  const { hasPermission } = useAuth();
  const { t } = useTranslation();

  const [tab, setTab] = useState<MobileTab>('attendance');
  // The picked company; falls back to the first until the user chooses.
  const [companyChoice, setCompanyChoice] = useState<number | null>(null);
  const company = useMemo(
    () => companies.find((c) => c.id === companyChoice) ?? companies[0],
    [companies, companyChoice],
  );

  const [athletes, setAthletes] = useState<Athlete[]>([]);
  // Subscription seat usage for the picked club (see
  // `features/subscription-seats.md`) — the same figures the desktop profile page
  // shows, since a club admin working from a phone answers for the same plan.
  const [seats, setSeats] = useState<CompanySeatUsage | null>(null);
  const [classes, setClasses] = useState<TrainingClass[]>([]);
  const [enrollments, setEnrollments] = useState<TrainingClassEnrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [snapshot, roster, usage] = await Promise.all([
        fetchTrainingClasses(company.id),
        fetchAthletes(company.id),
        fetchCompanySeatUsage(company.id),
      ]);
      setClasses(snapshot.classes);
      setEnrollments(snapshot.enrollments);
      setAthletes(roster);
      setSeats(usage);
      setError(null);
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : tRef.current('admin.trainingClasses.errors.load'),
      );
    } finally {
      setLoading(false);
    }
  }, [company.id]);

  useEffect(() => {
    void load();
  }, [load]);

  // Refresh just the roster — used after a person is registered or edited. Seat
  // usage moves with it (registering someone is exactly what changes the count),
  // so the two refresh together.
  const reloadRoster = useCallback(async () => {
    try {
      const [roster, usage] = await Promise.all([
        fetchAthletes(company.id),
        fetchCompanySeatUsage(company.id),
      ]);
      setAthletes(roster);
      setSeats(usage);
    } catch {
      // Non-fatal: both refresh on the next full load.
    }
  }, [company.id]);

  // Apply the refreshed company snapshot a class mutation (e.g. an enrolment)
  // responds with; the roster reloads alongside so new names resolve.
  const applySnapshot = useCallback(
    (snapshot: TrainingClassesSnapshot) => {
      setClasses(snapshot.classes);
      setEnrollments(snapshot.enrollments);
      void reloadRoster();
    },
    [reloadRoster],
  );

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="sticky" elevation={0}>
        <Toolbar sx={{ gap: 1 }}>
          <BrandLogo />
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography variant="subtitle1" noWrap sx={{ lineHeight: 1.2 }}>
              {t('admin.layout.brand')}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ display: 'block' }}
            >
              {company.name}
            </Typography>
          </Box>
          <LanguageSelector />
          {/* Full site and sign-out fold into the account menu here too: on a
              phone-width bar every icon spared is room for the club name. */}
          <AccountMenu
            onFullSite={onFullSite}
            fullSiteLabel={t('admin.mobile.fullSite')}
          />
        </Toolbar>
      </AppBar>

      {/* pb clears the fixed bottom navigation (and the roster FAB above it). */}
      <Box sx={{ flexGrow: 1, px: 2, py: 2, pb: 12 }}>
        <Stack spacing={2}>
          {companies.length > 1 && (
            <TextField
              select
              fullWidth
              size="small"
              label={t('admin.mobile.companyLabel')}
              value={String(company.id)}
              onChange={(e) => setCompanyChoice(Number(e.target.value))}
            >
              {companies.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {seats && <SeatUsageSummary usage={seats} dense />}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {tab === 'attendance' && (
                <MobileAttendanceView
                  classes={classes}
                  enrollments={enrollments}
                  athletes={athletes}
                  canManage={hasPermission('MANAGE_TRAINING_CLASSES')}
                  onSnapshot={applySnapshot}
                />
              )}
              {(tab === 'athletes' || tab === 'coaches') && (
                <MobileRosterView
                  companyId={company.id}
                  athletes={athletes}
                  isCoach={tab === 'coaches'}
                  canManage={hasPermission('MANAGE_ATHLETES')}
                  onChanged={() => void reloadRoster()}
                  classes={classes}
                  enrollments={enrollments}
                  canEnroll={hasPermission('MANAGE_TRAINING_CLASSES')}
                  onSnapshot={applySnapshot}
                />
              )}
            </>
          )}
        </Stack>
      </Box>

      <Paper
        elevation={3}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: (th) => th.zIndex.appBar,
        }}
      >
        <BottomNavigation
          showLabels
          value={tab}
          onChange={(_e, next: MobileTab) => setTab(next)}
        >
          <BottomNavigationAction
            value="attendance"
            label={t('admin.mobile.nav.attendance')}
            icon={<FactCheckIcon />}
          />
          <BottomNavigationAction
            value="athletes"
            label={t('admin.mobile.nav.athletes')}
            icon={<GroupsIcon />}
          />
          <BottomNavigationAction
            value="coaches"
            label={t('admin.mobile.nav.coaches')}
            icon={<SportsIcon />}
          />
        </BottomNavigation>
      </Paper>
    </Box>
  );
}
